import { useCallback, useEffect, useState } from 'react';
import { Toolbar } from './components/Toolbar';
import { ObjectList } from './components/ObjectList';
import { SettingsPanel } from './components/SettingsPanel';
import { PlateViewer } from './scene/PlateViewer';
import { useAppStore, store } from './store/appStore';
import { loadMeshFile } from './import/loaders';

export default function App() {
  const settingsOpen = useAppStore((s) => s.settingsOpen);
  const settingsPanelVisible = useAppStore((s) => s.settingsPanelVisible);
  const objectsPanelVisible = useAppStore((s) => s.objectsPanelVisible);
  const [dragging, setDragging] = useState(false);

  const ingestFiles = useCallback(async (files: FileList | File[]) => {
    const list = Array.from(files);
    for (const f of list) {
      try {
        const mesh = await loadMeshFile(f);
        store.addMesh(mesh.name, mesh.positions);
      } catch (e) {
        console.error(e);
        window.dispatchEvent(
          new CustomEvent('p1s-toast', { detail: `Failed: ${(e as Error).message}` })
        );
      }
    }
  }, []);

  useEffect(() => {
    const onDragOver = (e: DragEvent) => {
      if (!e.dataTransfer?.types?.includes('Files')) return;
      e.preventDefault();
      setDragging(true);
    };
    const onDragLeave = (e: DragEvent) => {
      if (e.target === document.documentElement || e.target === document.body) setDragging(false);
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      setDragging(false);
      if (e.dataTransfer?.files?.length) void ingestFiles(e.dataTransfer.files);
    };
    window.addEventListener('dragover', onDragOver);
    window.addEventListener('dragleave', onDragLeave);
    window.addEventListener('drop', onDrop);
    return () => {
      window.removeEventListener('dragover', onDragOver);
      window.removeEventListener('dragleave', onDragLeave);
      window.removeEventListener('drop', onDrop);
    };
  }, [ingestFiles]);

  // Share Target (PWA): /?share-target=1 with POST redirected to GET + files via launchQueue / form
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('share-target') === '1') {
      // Files arrive via launchQueue when supported
      const lq = (window as unknown as { launchQueue?: { setConsumer: (cb: (d: { files: File[] }) => void) => void } }).launchQueue;
      lq?.setConsumer(async (entry) => {
        if (entry.files?.length) await ingestFiles(entry.files);
      });
    }
  }, [ingestFiles]);

  const appClass = [
    'app',
    !objectsPanelVisible ? 'hide-objects' : '',
    !settingsPanelVisible ? 'hide-settings' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={appClass} data-testid="app">
      <Toolbar onFiles={ingestFiles} />
      <div className="workspace" data-testid="workspace">
        <div className="object-list-wrap" data-testid="object-list-wrap">
          <ObjectList />
        </div>
        <main className="viewport" data-testid="viewport">
          <PlateViewer />
          {dragging && <div className="drop-overlay">Drop STL / 3MF / OBJ</div>}
        </main>
        <div
          className={`settings-wrap ${settingsOpen ? 'open' : ''}`}
          data-testid="settings-wrap"
          onPointerDown={(e) => e.stopPropagation()}
          onTouchStart={(e) => e.stopPropagation()}
        >
          <div
            className="sheet-handle"
            role="button"
            aria-label="Toggle settings sheet"
            onClick={() => store.set({ settingsOpen: !store.getSnapshot().settingsOpen })}
          />
          <SettingsPanel />
        </div>
      </div>
    </div>
  );
}
